import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, CATEGORIES } from "../lib/api";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Plus, Trash2, Pencil, X } from "lucide-react";

const emptyShop = { name: "", category: "women", description: "", website: "", address: "", city: "", lat: 46.6, lng: 2.5, logo_url: "", phone: "", email: "" };
const emptyBlog = { title: "", content: "", excerpt: "", cover_image: "", author: "Rédaction" };

const tab = (active) => `px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${active ? "bg-[var(--agt-navy)] text-white" : "bg-white border border-[var(--agt-line)] text-[var(--agt-navy)] hover:bg-[var(--agt-bg-soft)]"}`;
const inp = "w-full px-4 py-2.5 rounded-xl border-2 border-[var(--agt-line)] bg-white focus:outline-none focus:border-[var(--agt-mustard)] focus:ring-2 focus:ring-[var(--agt-mustard)]/20 text-sm";

export default function Admin() {
  const { user, loading } = useAuth();
  const [tabKey, setTabKey] = useState("shops");
  const [shops, setShops] = useState([]);
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [editShop, setEditShop] = useState(null);
  const [editBlog, setEditBlog] = useState(null);

  const reloadShops = () => api.get("/shops").then((r) => setShops(r.data));
  const reloadBlog = () => api.get("/blog").then((r) => setPosts(r.data));
  const reloadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => setSubs([]));
  const reloadMsgs = () => api.get("/admin/contacts").then((r) => setMsgs(r.data)).catch(() => setMsgs([]));

  useEffect(() => {
    if (user?.role === "admin") {
      reloadShops(); reloadBlog(); reloadSubs(); reloadMsgs();
    }
  }, [user]);

  if (loading) return <main className="p-10">Chargement…</main>;
  if (!user || user.role !== "admin") return <Navigate to="/connexion" replace/>;

  const saveShop = async () => {
    try {
      if (editShop.id) {
        const { id, approved, owner_id, created_at, ...body } = editShop;
        await api.put(`/shops/${id}`, body);
      } else {
        await api.post("/shops", editShop);
      }
      toast.success("Boutique enregistrée");
      setEditShop(null); reloadShops();
    } catch (e) { toast.error("Erreur"); }
  };
  const deleteShop = async (id) => {
    if (!confirm("Supprimer cette boutique ?")) return;
    await api.delete(`/shops/${id}`); toast.success("Supprimée"); reloadShops();
  };

  const saveBlog = async () => {
    try {
      if (editBlog.id) {
        const { id, slug, created_at, published, ...body } = editBlog;
        await api.put(`/blog/${id}`, body);
      } else {
        await api.post("/blog", editBlog);
      }
      toast.success("Article enregistré"); setEditBlog(null); reloadBlog();
    } catch { toast.error("Erreur"); }
  };
  const deleteBlog = async (id) => {
    if (!confirm("Supprimer cet article ?")) return;
    await api.delete(`/blog/${id}`); toast.success("Supprimé"); reloadBlog();
  };

  return (
    <main className="max-w-7xl mx-auto px-6 py-14" data-testid="admin-page">
      <span className="eyebrow">Coulisses</span>
      <h1 className="font-heading mt-4 text-[var(--agt-navy)]" style={{ fontSize: "clamp(2.5rem, 6vw, 4rem)" }}>Espace admin.</h1>
      <div className="mt-6 flex flex-wrap gap-2">
        <button className={tab(tabKey === "shops")} onClick={() => setTabKey("shops")} data-testid="admin-tab-shops">Boutiques ({shops.length})</button>
        <button className={tab(tabKey === "blog")} onClick={() => setTabKey("blog")} data-testid="admin-tab-blog">Blog ({posts.length})</button>
        <button className={tab(tabKey === "subs")} onClick={() => setTabKey("subs")} data-testid="admin-tab-subs">Abonnés ({subs.length})</button>
        <button className={tab(tabKey === "msgs")} onClick={() => setTabKey("msgs")} data-testid="admin-tab-msgs">Messages ({msgs.length})</button>
      </div>

      <div className="mt-8">
        {tabKey === "shops" && (
          <div>
            <button onClick={() => setEditShop({ ...emptyShop })} className="btn-primary mb-4" data-testid="admin-new-shop"><Plus className="w-4 h-4"/> Nouvelle boutique</button>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shops.map((s) => {
                const c = CATEGORIES.find((x) => x.key === s.category);
                return (
                  <div key={s.id} className="card-soft p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: c.color + "22", color: c.color }}>{c.label}</span>
                        <h3 className="font-bold mt-1">{s.name}</h3>
                        <p className="text-xs text-stone-500">{s.city}</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditShop(s)} className="p-2 rounded-full hover:bg-pink-50" data-testid={`admin-edit-shop-${s.id}`}><Pencil className="w-4 h-4"/></button>
                        <button onClick={() => deleteShop(s.id)} className="p-2 rounded-full hover:bg-red-50 text-red-600" data-testid={`admin-del-shop-${s.id}`}><Trash2 className="w-4 h-4"/></button>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-stone-600 line-clamp-2">{s.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tabKey === "blog" && (
          <div>
            <button onClick={() => setEditBlog({ ...emptyBlog })} className="btn-primary mb-4" data-testid="admin-new-blog"><Plus className="w-4 h-4"/> Nouvel article</button>
            <div className="grid md:grid-cols-2 gap-4">
              {posts.map((p) => (
                <div key={p.id} className="card-soft p-4 flex gap-4">
                  {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-24 h-24 object-cover rounded-xl"/>}
                  <div className="flex-1">
                    <h3 className="font-bold">{p.title}</h3>
                    <p className="text-xs text-stone-500 mt-1">/{p.slug}</p>
                    <p className="text-xs text-stone-600 line-clamp-2 mt-1">{p.excerpt}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditBlog(p)} className="p-2 rounded-full hover:bg-pink-50"><Pencil className="w-4 h-4"/></button>
                    <button onClick={() => deleteBlog(p.id)} className="p-2 rounded-full hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4"/></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tabKey === "subs" && (
          <div className="card-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-pink-50 text-left">
                <tr><th className="p-3">Nom</th><th className="p-3">Email</th><th className="p-3">Statut</th><th className="p-3">Créé</th></tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-pink-50"><td className="p-3">{s.name}</td><td className="p-3">{s.email}</td><td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>{s.subscription_status}</span></td><td className="p-3 text-stone-500 text-xs">{s.created_at?.slice(0,10)}</td></tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-stone-500">Aucun abonné</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tabKey === "msgs" && (
          <div className="grid md:grid-cols-2 gap-4">
            {msgs.map((m) => (
              <div key={m.id} className="card-soft p-4">
                <div className="flex justify-between text-xs text-stone-500"><span>{m.name} · {m.email}</span><span>{m.created_at?.slice(0,10)}</span></div>
                <h4 className="font-bold mt-1">{m.subject}</h4>
                <p className="text-sm text-stone-700 mt-2 whitespace-pre-line">{m.message}</p>
              </div>
            ))}
            {msgs.length === 0 && <p className="text-stone-500">Aucun message.</p>}
          </div>
        )}
      </div>

      {editShop && (
        <Modal onClose={() => setEditShop(null)} title={editShop.id ? "Éditer la boutique" : "Nouvelle boutique"}>
          <div className="grid grid-cols-2 gap-3">
            <input className={inp} placeholder="Nom" value={editShop.name} onChange={(e) => setEditShop({ ...editShop, name: e.target.value })}/>
            <select className={inp} value={editShop.category} onChange={(e) => setEditShop({ ...editShop, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <input className={inp + " col-span-2"} placeholder="Site web" value={editShop.website} onChange={(e) => setEditShop({ ...editShop, website: e.target.value })}/>
            <input className={inp} placeholder="Ville" value={editShop.city} onChange={(e) => setEditShop({ ...editShop, city: e.target.value })}/>
            <input className={inp} placeholder="Adresse" value={editShop.address} onChange={(e) => setEditShop({ ...editShop, address: e.target.value })}/>
            <input className={inp} type="number" step="0.0001" placeholder="Latitude" value={editShop.lat} onChange={(e) => setEditShop({ ...editShop, lat: parseFloat(e.target.value) })}/>
            <input className={inp} type="number" step="0.0001" placeholder="Longitude" value={editShop.lng} onChange={(e) => setEditShop({ ...editShop, lng: parseFloat(e.target.value) })}/>
            <textarea rows={4} className={inp + " col-span-2"} placeholder="Description" value={editShop.description} onChange={(e) => setEditShop({ ...editShop, description: e.target.value })}/>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setEditShop(null)} className="btn-ghost">Annuler</button>
            <button onClick={saveShop} className="btn-primary" data-testid="admin-save-shop">Enregistrer</button>
          </div>
        </Modal>
      )}

      {editBlog && (
        <Modal onClose={() => setEditBlog(null)} title={editBlog.id ? "Éditer l'article" : "Nouvel article"}>
          <div className="grid gap-3">
            <input className={inp} placeholder="Titre" value={editBlog.title} onChange={(e) => setEditBlog({ ...editBlog, title: e.target.value })}/>
            <input className={inp} placeholder="Image de couverture (URL)" value={editBlog.cover_image} onChange={(e) => setEditBlog({ ...editBlog, cover_image: e.target.value })}/>
            <input className={inp} placeholder="Auteur" value={editBlog.author} onChange={(e) => setEditBlog({ ...editBlog, author: e.target.value })}/>
            <textarea rows={2} className={inp} placeholder="Extrait" value={editBlog.excerpt} onChange={(e) => setEditBlog({ ...editBlog, excerpt: e.target.value })}/>
            <textarea rows={8} className={inp} placeholder="Contenu" value={editBlog.content} onChange={(e) => setEditBlog({ ...editBlog, content: e.target.value })}/>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setEditBlog(null)} className="btn-ghost">Annuler</button>
            <button onClick={saveBlog} className="btn-primary" data-testid="admin-save-blog">Enregistrer</button>
          </div>
        </Modal>
      )}
    </main>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-editorial text-2xl font-bold">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-pink-50"><X className="w-5 h-5"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
